import { useEffect, useMemo, useState } from "react";
import AppCheckbox from "@/ui/components/Checkbox";
import FilterBar from "@/ui/components/FilterBar";
import PageHeader from "@/ui/components/PageHeader";
import ProfitTable from "@/ui/components/ProfitTable";
import type {
  WoodcuttingBuffOptions,
  WoodcuttingBuffsMarketData,
} from "@/types/WoodcuttingTypes";
import Coins from "@/ui/components/Coins";
import { effiencyNumberOfActions } from "@/utils/util";
import MarketplaceTaxInput from "@/ui/components/MarketplaceTaxInput";
import {
  applyMarketplaceTax,
  DEFAULT_MARKETPLACE_TAX_PERCENT,
} from "@/utils/marketplaceTax";

type WoodcuttingEntry = {
  name: string;
  outputCountMin: number;
  outputCountMax: number;
  actionTimeSeconds: number;
  outputSellPrice: number;
  branchOfInsightDropChance: number;
};

type BranchOfInsightProps = {
  marketSell: number;
  dropChance: number;
  includeInCalculation: boolean;
};

function computeProfitPer10s(
  entry: WoodcuttingEntry,
  buffOptions: WoodcuttingBuffOptions,
  branchOfInsight: BranchOfInsightProps,
  marketplaceTaxPercent: number,
): number {
  const totalEfficiencyBonus =
    (buffOptions.useWoodcuttingTea ? 2 : 0) +
    (buffOptions.useSuperWoodcuttingTea ? 4 : 0) +
    (buffOptions.useUltraWoodcuttingTea ? 6 : 0) +
    (buffOptions.useEfficiencyTea ? 10 : 0);

  const effectiveActionTime = entry.actionTimeSeconds;
  const averageOutput = (entry.outputCountMin + entry.outputCountMax) / 2;
  const numberOfActions = effiencyNumberOfActions(totalEfficiencyBonus);
  const effectiveOutput =
    averageOutput * (buffOptions.useGatheringTea ? 1.15 : 1) * numberOfActions;

  // Processing Tea (15% chance to convert to processed material) is deferred
  // pending a mapping of raw resource to processed item per action.

  const itemsPer10s = (effectiveOutput / effectiveActionTime) * 10;
  const outputSellPriceAfterTax = applyMarketplaceTax(
    entry.outputSellPrice,
    marketplaceTaxPercent,
  );
  const revenuePer10s = itemsPer10s * outputSellPriceAfterTax;

  // Buffs last 300 seconds. Cost is spread over that duration (300s / 10s = 30).
  const buffCostPer10s =
    [
      buffOptions.useWoodcuttingTea ? buffOptions.woodcuttingTeaPrice : 0,
      buffOptions.useSuperWoodcuttingTea
        ? buffOptions.superWoodcuttingTeaPrice
        : 0,
      buffOptions.useUltraWoodcuttingTea
        ? buffOptions.ultraWoodcuttingTeaPrice
        : 0,
      buffOptions.useGatheringTea ? buffOptions.gatheringTeaPrice : 0,
      buffOptions.useProcessingTea ? buffOptions.processingTeaPrice : 0,
      buffOptions.useEfficiencyTea ? buffOptions.efficiencyTeaPrice : 0,
    ].reduce((sum, cost) => sum + cost, 0) / 30;

  const branchOfInsightProfitPer10s =
    ((branchOfInsight.dropChance * numberOfActions) / effectiveActionTime) * 10;
  const branchOfInsightSellPriceAfterTax = applyMarketplaceTax(
    branchOfInsight.marketSell,
    marketplaceTaxPercent,
  );
  const branchOfInsightProfit: number =
    branchOfInsightProfitPer10s * branchOfInsightSellPriceAfterTax;

  return (
    revenuePer10s -
    buffCostPer10s +
    branchOfInsightProfit * Number(branchOfInsight.includeInCalculation)
  );
}

export default function WoodcuttingPage() {
  const [filter, setFilter] = useState("");
  const [entries, setEntries] = useState<WoodcuttingEntry[]>([]);
  const [branchOfInsightSellPrice, setBranchOfInsightSellPrice] = useState(0);
  const [rareFindModifier, setRareFindModifier] = useState(0);
  const [marketplaceTaxPercent, setMarketplaceTaxPercent] = useState(
    DEFAULT_MARKETPLACE_TAX_PERCENT,
  );

  const [useWoodcuttingTea, setUseWoodcuttingTea] = useState(false);
  const [useSuperWoodcuttingTea, setUseSuperWoodcuttingTea] = useState(false);
  const [useUltraWoodcuttingTea, setUseUltraWoodcuttingTea] = useState(false);
  const [useGatheringTea, setUseGatheringTea] = useState(false);
  const [useProcessingTea, setUseProcessingTea] = useState(false);
  const [useEfficiencyTea, setUseEfficiencyTea] = useState(false);
  const [useBranchOfInsightInProfits, setUseBranchOfInsightInProfits] =
    useState(false);

  const [buffMarketData, setBuffMarketData] =
    useState<WoodcuttingBuffsMarketData | null>(null);

  async function loadBuffSellPrices() {
    const res = await fetch("/api/market/woodcutting_buffs");
    if (!res.ok) return;
    const data: WoodcuttingBuffsMarketData = await res.json();
    setBuffMarketData(data);
  }

  async function loadEntries() {
    const res = await fetch("/api/woodcutting/profit");
    if (!res.ok) {
      setEntries([]);
      return;
    }

    const data = await res.json();

    const mapped: WoodcuttingEntry[] = data.actions.map((action: any) => ({
      name: action.action,
      outputCountMin: action.outputCountMin,
      outputCountMax: action.outputCountMax,
      actionTimeSeconds: action.actionTimeSeconds,
      outputSellPrice: action.outputSellPrice,
      branchOfInsightDropChance: action.branchOfInsightDropChance,
    }));

    setEntries(mapped);
    setBranchOfInsightSellPrice(data.branchOfInsightSellPrice);
  }

  useEffect(() => {
    loadEntries();
    loadBuffSellPrices();
  }, []);

  const buffOptions: WoodcuttingBuffOptions = {
    useWoodcuttingTea,
    woodcuttingTeaPrice: buffMarketData?.woodcuttingTeaSell ?? NaN,
    useSuperWoodcuttingTea,
    superWoodcuttingTeaPrice: buffMarketData?.superWoodcuttingTeaSell ?? NaN,
    useUltraWoodcuttingTea,
    ultraWoodcuttingTeaPrice: buffMarketData?.ultraWoodcuttingTeaSell ?? NaN,
    useGatheringTea,
    gatheringTeaPrice: buffMarketData?.gatheringTeaSell ?? NaN,
    useProcessingTea,
    processingTeaPrice: buffMarketData?.processingTeaSell ?? NaN,
    useEfficiencyTea,
    efficiencyTeaPrice: buffMarketData?.efficiencyTeaSell ?? NaN,
  };

  const filteredEntries = useMemo(() => {
    const withProfit = entries
      .map((entry) => ({
        ...entry,
        profitPer10s: computeProfitPer10s(
          entry,
          buffOptions,
          {
            dropChance: computeEffectiveDropChance(entry),
            marketSell: branchOfInsightSellPrice,
            includeInCalculation: useBranchOfInsightInProfits,
          },
          marketplaceTaxPercent,
        ),
      }))
      .sort((a, b) => b.profitPer10s - a.profitPer10s);

    if (filter.trim() === "") return withProfit;

    return withProfit.filter((entry) =>
      entry.name.toLowerCase().includes(filter.toLowerCase()),
    );
  }, [
    entries,
    filter,
    useWoodcuttingTea,
    useSuperWoodcuttingTea,
    useUltraWoodcuttingTea,
    useGatheringTea,
    useProcessingTea,
    useEfficiencyTea,
    buffMarketData,
    useBranchOfInsightInProfits,
    marketplaceTaxPercent,
  ]);

  function computeEffectiveDropChance(entry: WoodcuttingEntry): number {
    return entry.branchOfInsightDropChance * (1 + rareFindModifier / 100);
  }

  return (
    <div className="mt-4 px-4 sm:px-6 lg:px-8">
      <PageHeader
        title="Woodcutting Profit Calculator"
        description="This list shows the potential profits for each woodcutting action."
      />

      {/* MARKETPLACE */}
      <div className="mt-8 space-y-3">
        <h2>Marketplace</h2>
        <MarketplaceTaxInput
          value={marketplaceTaxPercent}
          onChange={setMarketplaceTaxPercent}
        />
      </div>

      {/* BUFFS */}
      <div className="mt-8 space-y-3">
        <h2>Buffs</h2>
        <AppCheckbox
          id="woodcutting_tea"
          label="Woodcutting Tea"
          description="2% Efficiency bonus and increases Woodcutting level by 3 for 300 seconds"
          checked={useWoodcuttingTea}
          onChange={setUseWoodcuttingTea}
        />
        <AppCheckbox
          id="super_woodcutting_tea"
          label="Super Woodcutting Tea"
          description="4% Efficiency bonus and increases Woodcutting level by 6 for 300 seconds"
          checked={useSuperWoodcuttingTea}
          onChange={setUseSuperWoodcuttingTea}
        />
        <AppCheckbox
          id="ultra_woodcutting_tea"
          label="Ultra Woodcutting Tea"
          description="6% Efficiency bonus and increases Woodcutting level by 8 for 300 seconds"
          checked={useUltraWoodcuttingTea}
          onChange={setUseUltraWoodcuttingTea}
        />
        <AppCheckbox
          id="gathering_tea"
          label="Gathering Tea"
          description="15% increase in gathering quantity for 300 seconds"
          checked={useGatheringTea}
          onChange={setUseGatheringTea}
        />
        <AppCheckbox
          id="processing_tea"
          label="Processing Tea"
          description="15% chance to convert gathered resources into processed material for 300 seconds"
          checked={useProcessingTea}
          onChange={setUseProcessingTea}
        />
        <AppCheckbox
          id="efficiency_tea"
          label="Efficiency Tea"
          description="10% Efficiency bonus for 300 seconds"
          checked={useEfficiencyTea}
          onChange={setUseEfficiencyTea}
        />
        <h2>Rare drops</h2>
        <AppCheckbox
          id="include_branch_of_insight_profit"
          label="Include Branch Of Insight drop in profits."
          description={
            <span>
              Market sell value are <Coins value={branchOfInsightSellPrice} />{" "}
              coins.
            </span>
          }
          checked={useBranchOfInsightInProfits}
          onChange={setUseBranchOfInsightInProfits}
        />
      </div>

      {/* FILTER */}
      <FilterBar value={filter} onChange={setFilter} showSortButton={false} />

      {/* TABLE */}
      <ProfitTable
        columns={[
          "Tree",
          "Profit per 10s",
          ...(useBranchOfInsightInProfits
            ? ["Branch of Insight Drop Chance"]
            : []),
          "Sell value",
          "Action Time Seconds",
        ]}
      >
        {filteredEntries.map((entry) => (
          <tr key={entry.name}>
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-900">
              {entry.name}
            </td>
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
              <Coins value={entry.profitPer10s} />
            </td>
            {useBranchOfInsightInProfits && (
              <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                {computeEffectiveDropChance(entry).toFixed(4)}%
              </td>
            )}
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
              <Coins value={entry.outputSellPrice} />
            </td>
            <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
              <Coins value={entry.actionTimeSeconds} />
            </td>
          </tr>
        ))}
      </ProfitTable>
    </div>
  );
}
